/**
 * Connection State Service - Manages network and API health state
 * 
 * Responsibilities:
 * - Track online/offline status (navigator.onLine)
 * - Track API health (circuit breaker pattern)
 * - Calculate sync status ('online' | 'apiDown' | 'offline')
 * - Notify listeners of status changes
 * 
 * Architecture:
 * - Infrastructure layer (no domain dependencies)
 * - Used by ServerSyncService and UI components
 */

import { logger } from '../utils/logger'

export type SyncStatus = 'online' | 'apiDown' | 'offline'

interface StatusListener {
  callback: (status: SyncStatus) => void
}

export class ConnectionStateService {
  private isOnline = navigator.onLine
  private isApiHealthy = true
  private syncStatus: SyncStatus = 'online'
  private statusListeners: Set<StatusListener> = new Set()

  async initialize(): Promise<void> {
    // Listen for browser online/offline events
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)
    logger.debug('[ConnectionState] Initialized')
  }

  /**
   * Get current sync status
   */
  getSyncStatus(): SyncStatus {
    return this.syncStatus
  }

  /**
   * Get current online status (network connectivity)
   */
  isNetworkOnline(): boolean {
    return this.isOnline
  }

  /**
   * Get current API health status
   */
  isApiUp(): boolean {
    return this.isApiHealthy
  }

  /**
   * Mark API as down (circuit breaker opened)
   * Call this when consecutive failures exceed threshold
   */
  markApiDown(): void {
    if (this.isApiHealthy) {
      logger.warn('[ConnectionState] API marked as down (circuit breaker opened)')
      this.isApiHealthy = false
      this.updateSyncStatus()
    }
  }

  /**
   * Mark API as healthy (circuit breaker closed)
   * Call this when API request succeeds
   */
  markApiHealthy(): void {
    if (!this.isApiHealthy) {
      logger.info('[ConnectionState] API marked as healthy (circuit breaker closed)')
      this.isApiHealthy = true
      this.updateSyncStatus()
    }
  }

  /**
   * Register a listener for status changes
   * Returns unsubscribe function
   */
  onStatusChange(callback: (status: SyncStatus) => void): () => void {
    const listener: StatusListener = { callback }
    this.statusListeners.add(listener)
    
    // Immediately notify with current status (catch errors to prevent registration failure)
    try {
      callback(this.syncStatus)
    } catch (error) {
      logger.error('[ConnectionState] Error in status listener during registration:', error)
    }
    
    return () => this.statusListeners.delete(listener)
  }

  /**
   * Cleanup event listeners (call on app shutdown)
   */
  cleanup(): void {
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOffline)
    this.statusListeners.clear()
  }

  // Private methods

  private handleOnline = (): void => {
    logger.debug('[ConnectionState] Network online')
    this.isOnline = true
    // Assume API is healthy until proven otherwise
    this.isApiHealthy = true
    this.updateSyncStatus()
  }

  private handleOffline = (): void => {
    logger.debug('[ConnectionState] Network offline')
    this.isOnline = false
    this.isApiHealthy = false // If offline, API is also unreachable
    this.updateSyncStatus()
  }

  private updateSyncStatus(): void {
    const oldStatus = this.syncStatus

    // Calculate new status
    if (!this.isOnline) {
      this.syncStatus = 'offline'
    } else if (!this.isApiHealthy) {
      this.syncStatus = 'apiDown'
    } else {
      this.syncStatus = 'online'
    }

    // Notify listeners if status changed
    if (oldStatus !== this.syncStatus) {
      logger.debug(`[ConnectionState] Sync status changed: ${oldStatus} -> ${this.syncStatus}`)
      this.notifyStatusListeners()
    }
  }

  private notifyStatusListeners(): void {
    this.statusListeners.forEach(listener => {
      try {
        listener.callback(this.syncStatus)
      } catch (error) {
        logger.error('[ConnectionState] Error in status listener:', error)
      }
    })
  }
}

// Singleton instance
export const connectionStateService = new ConnectionStateService()
